import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Alert,
  TextField
} from '@mui/material'
import { Vehicle } from '@otw/models'
import { PrivateUserInfoType } from '@otw/models'
import { UserSelectionTab } from './UserSelectionTab'
import { VehicleSelectionTab } from './VehicleSelectionTab'
import { usePhotoUploads } from 'hooks/admin/usePhotoUploads'
import { useSnackbar } from '@/hooks/common/useSnackbar'

interface CreatePhotoUploadDialogProps {
  open: boolean
  onClose: () => void
}

const steps = ['Select User', 'Select Vehicles', 'Create Photo Upload']

export const CreatePhotoUploadDialog: React.FC<CreatePhotoUploadDialogProps> = ({
  open,
  onClose
}) => {
  const [activeStep, setActiveStep] = useState(0)
  const [selectedUser, setSelectedUser] = useState<PrivateUserInfoType | null>(null)
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([])
  const [selectedVehicleObjects, setSelectedVehicleObjects] = useState<Vehicle[]>([])
  const [password, setPassword] = useState('')

  const { createPhotoUpload, isCreatingPhotoUpload, getAccessLink } = usePhotoUploads()
  const { showSuccess } = useSnackbar()

  const handleReset = () => {
    setActiveStep(0)
    setSelectedUser(null)
    setSelectedVehicles([])
    setSelectedVehicleObjects([])
    setPassword('')
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleVehicleToggle = (vehicleId: string) => {
    setSelectedVehicles(prev => 
      prev.includes(vehicleId) 
        ? prev.filter(id => id !== vehicleId) 
        : [...prev, vehicleId]
    )
  }

  const handleCreatePhotoUpload = () => {
    if (selectedUser && selectedVehicleObjects.length > 0 && password.trim()) {
      createPhotoUpload(
        { 
          user: selectedUser, 
          vehicles: selectedVehicleObjects,
          password: password.trim()
        },
        {
          onSuccess: (result) => {
            try {
              const link = result?.link || (result?.accessCode ? getAccessLink(result.accessCode) : '')
              if (link) {
                navigator.clipboard.writeText(link)
                showSuccess('Access link copied to clipboard',{autoHideDuration: 2000})
              }
            } catch {}
            handleClose()
          }
        }
      )
    }
  }

  const canProceedToVehicles = selectedUser !== null
  const canCreateUpload = selectedUser !== null && selectedVehicleObjects.length > 0 && password.trim().length > 0

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <UserSelectionTab
            selectedUser={selectedUser}
            onUserSelect={setSelectedUser}
            onNext={handleNext}
          />
        )
      case 1:
        return (
          <VehicleSelectionTab
            selectedVehicles={selectedVehicles}
            selectedUser={selectedUser}
            onVehicleToggle={handleVehicleToggle}
            onVehiclesSelect={setSelectedVehicleObjects}
          />
        )
      case 2:
        return (
          <Box sx={{ py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Create Photo Upload
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              You are about to create a photo upload session for:
            </Alert>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>User:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedUser?.first_name} {selectedUser?.last_name} ({selectedUser?.email})
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Vehicles ({selectedVehicleObjects.length}):</strong>
              </Typography>
              {selectedVehicleObjects.map(vehicle => (
                <Typography key={vehicle.id} variant="body2" color="text.secondary">
                  • {vehicle.year} {vehicle.make} {vehicle.model} (VIN: {vehicle.vin})
                </Typography>
              ))}
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password for photo upload session"
                required
                helperText="This password will be required to access the photo upload session"
              />
            </Box>

            <Alert severity="warning">
              This will assign the selected vehicles to the user and send them a photo upload link with the specified password.
            </Alert>
          </Box>
        )
      default:
        return 'Unknown step'
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        Create Photo Upload Session
      </DialogTitle>

      <Box sx={{ px: 3, pb: 2 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {getStepContent(activeStep)}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isCreatingPhotoUpload}>
            Back
          </Button>
        )}

        {activeStep < steps.length - 1 ? (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={
              (activeStep === 0 && !canProceedToVehicles) ||
              (activeStep === 1 && selectedVehicleObjects.length === 0)
            }
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleCreatePhotoUpload}
            variant="contained"
            disabled={!canCreateUpload || isCreatingPhotoUpload}
          >
            {isCreatingPhotoUpload ? 'Creating...' : 'Create Photo Upload'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
} 